import { BoxGeometry, BufferAttribute, BufferGeometry, Color, CylinderGeometry, Matrix4 } from 'three'
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js'

export class GeometryKit {
  private geometries: BufferGeometry[] = []

  add(geometry: BufferGeometry, color: string, matrix?: Matrix4): void {
    if (matrix) geometry.applyMatrix4(matrix)
    const flat = geometry.index ? geometry.toNonIndexed() : geometry
    if (flat !== geometry) geometry.dispose()
    flat.deleteAttribute('uv')
    const tint = new Color(color)
    const colors = new Float32Array(flat.getAttribute('position').count * 3)
    for (let i = 0; i < colors.length; i += 3) colors.set([tint.r, tint.g, tint.b], i)
    flat.setAttribute('color', new BufferAttribute(colors, 3))
    this.geometries.push(flat)
  }

  box(x: number, y: number, z: number, w: number, h: number, d: number, color: string, yaw = 0): void {
    const matrix = new Matrix4().makeRotationY(yaw).setPosition(x, y + h / 2, z)
    this.add(new BoxGeometry(w, h, d), color, matrix)
  }

  cylinder(x: number, y: number, z: number, radius: number, height: number, color: string, sides = 8): void {
    this.add(new CylinderGeometry(radius, radius, height, sides), color,
      new Matrix4().makeTranslation(x, y + height / 2, z))
  }

  finish(): BufferGeometry {
    if (this.geometries.length === 0) {
      const empty = new BufferGeometry()
      for (const name of ['position', 'normal', 'color']) empty.setAttribute(name, new BufferAttribute(new Float32Array(0), 3))
      return empty
    }
    const merged = mergeGeometries(this.geometries, false)!
    for (const geometry of this.geometries) geometry.dispose()
    this.geometries.length = 0
    return merged
  }
}

export function treeGeometry(seed: number, alien = false): BufferGeometry {
  const kit = new GeometryKit()
  kit.box(0, 0, 0, 0.16, 1.1, 0.17, '#6f7260')
  const tones = alien ? ['#687e54', '#96a960', '#637d60'] : ['#335d42', '#49774b', '#6d9150']
  const tall = seed % 3 === 0
  if (tall) {
    for (let level = 0; level < 4; level++) {
      const width = 1.35 - level * 0.28
      kit.box(0, 0.65 + level * 0.48, 0, width, 0.6, width, tones[level % 3])
    }
  } else {
    kit.box(0, 0.95, 0, 1.25, 0.8, 1.1, tones[0])
    kit.box(0.12, 1.65, -0.03, 0.92, 0.42, 0.86, tones[1])
    kit.box(-0.43, 1.1, 0.18, 0.52, 0.5, 0.65, tones[2])
    kit.box(0.44, 1.35, -0.11, 0.46, 0.5, 0.55, tones[1])
  }
  return kit.finish()
}

export function houseGeometry(variant: number): BufferGeometry {
  const kit = new GeometryKit()
  const height = variant === 2 ? 2.5 : 1.75
  kit.box(0, 0, 0, 2.9, 0.16, 2.7, '#969e96')
  kit.box(-1.34, 0.16, 1.2, 0.1, 1.8, 0.1, '#6e7a77')
  kit.box(-1.17, 1.96, 1.2, 0.44, 0.08, 0.18, '#d2d8c8')
  kit.box(0, 0.16, 0, 2.4, height, 2.1, variant === 1 ? '#d0d5ca' : '#c0c5b5')
  kit.box(0, 0.3, 1.065, 0.42, 0.9, 0.06, '#454f51')
  for (const x of [-0.76, 0.76]) {
    for (let row = 0; row < (variant === 2 ? 2 : 1); row++) {
      for (const side of [-1, 1]) {
        kit.box(x, 0.85 + row * 0.9, side * 1.07, 0.56, 0.5, 0.08, '#466670')
        kit.box(x, 0.8 + row * 0.9, side * 1.12, 0.66, 0.07, 0.16, '#e2e1cc')
      }
    }
    for (const side of [-1, 1]) kit.box(side * 1.22, 0.9, x, 0.06, 0.55, 0.48, '#466670')
  }
  if (variant === 2) {
    kit.box(0, height + 0.16, 0, 2.7, 0.22, 2.4, '#778284')
    kit.box(0.55, height + 0.38, -0.4, 0.6, 0.37, 0.6, '#a0aaa5')
    for (let i = 0; i < 5; i++) kit.box(0.55, height + 0.75, -0.64 + i * 0.1, 0.53, 0.03, 0.04, '#515c5e')
  } else {
    for (let step = 0; step < 4; step++) {
      kit.box(0, height + 0.16 + step * 0.18, 0, 2.76, 0.19, 2.6 - step * 0.58,
        step % 2 ? '#a26554' : '#b6755d')
    }
    kit.box(-0.7, height + 0.5, -0.4, 0.28, 0.65, 0.3, '#b8baae')
  }
  return kit.finish()
}
